/* eslint-disable no-useless-catch */
/* eslint-disable no-unused-vars */
import Vue from 'vue'

import { db } from '@/services/firebase.service'
import { SurveyManager } from '@/models/v1/managers'
import { parseISO, eachDayOfInterval } from 'date-fns'

import SurveyService from '@/services/survey.service'

// Helpers
import { clone } from '@/helpers'

const normalize = (objs, key = 'id') => {
  return objs.reduce((acc, value) => ({ ...acc, [value[key]]: value }), {})
}

function getWeek(startDate, endDate) {
  // const startDate = parse(start, 'yyyy-MM-dd', new Date())
  // const endDate = parse(end, 'yyyy-MM-dd', new Date())

  const start = parseISO(startDate)
  const end = parseISO(endDate)

  const days = eachDayOfInterval({ start, end })

  return {
    start,
    end,
    days,
  }
}

const state = {
  survey: null,
  loading: false,
  journey: null,
  journeysById: {},
  currentDay: 0,
  days: {
    0: { id: 0, abbr: 'Lu', journeysCount: 0 },
    1: { id: 1, abbr: 'Ma', journeysCount: 0 },
    2: { id: 2, abbr: 'Me', journeysCount: 0 },
    3: { id: 3, abbr: 'Gi', journeysCount: 0 },
    4: { id: 4, abbr: 'Ve', journeysCount: 0 },
    5: { id: 5, abbr: 'Sa', journeysCount: 0 },
    6: { id: 6, abbr: 'Do', journeysCount: 0 },
  },
}

const getters = {
  isExpired: (state) => !!(state.survey?.valid_from && state.survey?.valid_from <= new Date()),
  isSubmitted: (state) => !!state.survey?.submitted_at,
  isStarted: (state) => !!state.survey?.started_at,
  lastJourney: (state) => state.survey?.journeys?.slice(-1)?.[0], // TODO: rimuovere e usare getLastJourney
  journeysOfDay: (state) => (day) => {
    return state.survey?.journeys?.filter((j) => j.day === day)
  },
  crossedBorders: (state) => {
    return [
      ...new Set(
        state.survey?.journeys
          ?.filter((j) => j.type === 1)
          .map((j) => j.border_crossed?.name)
          .filter(Boolean) ?? []
      ),
    ]
  },
  totalDistances: (state) => {
    if (!state.survey?.journeys) return 0
    let manager = new SurveyManager(state.survey)
    let journeys = manager.getRestructuredJourneys()
    return manager.calculateTotalDistances(journeys)
  },
  // getJourneyById: (state) => (id) => {
  //   return state.survey.journeys.find((todo) => todo.id === id)
  // },
  getNextJourneyOf: (state) => (previousId) => {
    const x = state.survey.journeys

    return state.survey.journeys.find((j) => j.previous_id === previousId)
  },
  getWeekDates: (state) => getWeek(state.survey?.start_date, state.survey?.end_date),
  getWeekMap: (state) => {
    const week = getWeek(state.survey?.start_date, state.survey?.end_date)

    return week.days.map((day, index) => {
      const journeys = state.survey?.journeys?.filter((j) => j.day === index) ?? []

      return {
        id: index,
        dt: day,
        journeysIds: journeys.map((j) => j.id),
        journeysCount: journeys.length,
      }
    })
  },
  /* --- */
  hasJourneys: (state) => state.survey?.journeys?.length > 0 ?? null,
  getJourneys: (state) => state.survey?.journeys ?? [],
  getLastJourney: (state) => state.survey?.journeys?.slice(-1)?.[0],
  getFirstJourneyId: (state) => state.survey?.journeys?.[0]?.id ?? null,
  getLastJourneyId: (state) => state.survey?.journeys?.slice(-1)?.[0]?.id ?? null,
  getJourneyById: (state) => (id) => state.journeysById[id] || null,
  getPreviousJourney: (state) => (journey) => state.survey?.journeys?.find((j) => j.id === journey.previous_id),
  getNextJourney: (state) => (journey) => state.survey?.journeys?.find((j) => j.previous_id === journey.id),
  isFirstDay: (state) => state.currentDay === 0,
  isLastDay: (state) => state.currentDay === 6,
  getPrevDay: (state) => (state.currentDay > 0 ? state.currentDay - 1 : null),
  getNextDay: (state) => (state.currentDay < 6 ? state.currentDay + 1 : null),
  getCurrentDay: (state) => state.currentDay,
  getCurrentDayDate: (state, getters) => getters?.getWeekDates?.days?.[state.currentDay] ?? null,
  getPrevDayDate: (state, getters) => getters?.getWeekDates?.days?.[state.currentDay - 1] ?? null,
  getNextDayDate: (state, getters) => getters?.getWeekDates?.days?.[state.currentDay + 1] ?? null,
  getJourneysOfCurrentDay: (state) => state.survey?.journeys?.filter((o) => o.day === state.currentDay) ?? [],
}

const mutations = {
  SET_SURVEY(state, survey) {
    state.survey = survey
    state.journeysById = normalize(survey.journeys)
  },
  SET_JOURNEY(state, journey) {
    state.journey = journey
    Vue.set(state.journeysById, journey.id, journey)
  },
  SET_LOADING(state, flag) {
    state.loading = flag
  },
  PUSH_JOURNEY(state, journey) {
    state.survey.journeys.push(journey)
    Vue.set(state.journeysById, journey.id, journey)
  },
  POP_JOURNEY(state, journey) {
    state.survey.journeys.pop()
    Vue.delete(state.journeysById, journey.id)
  },
  SET_JOURNEYS(state, journeys) {
    state.survey.journeys = journeys
    state.journeysById = normalize(journeys)
  },
  GO_TO_NEXT_DAY(state) {
    if (state.currentDay === 6) return
    state.currentDay = state.currentDay + 1
  },
  GO_TO_PREV_DAY(state) {
    if (state.currentDay === 0) return
    state.currentDay = state.currentDay - 1
  },
  SET_CURRENT_DAY(state, day) {
    if (day !== state.currentDay) state.currentDay = day
  },
}

const actions = {
  async init({ commit }, id) {
    try {
      let surveysRef = db.collection('surveys')
      let surveyRef = await surveysRef.doc(id)

      surveyRef.onSnapshot(function (doc) {
        if (doc.exists) {
          commit('SET_SURVEY', { id: doc.id, ...doc.data() })
        }
      })
    } catch (error) {
      console.error('Error on init survey: ', error)
      throw error
    }
  },

  async update({ dispatch }, { id, data }) {
    try {
      await SurveyService.updateSurvey(id, data)

      // dispatch(
      //   'notification/showMessage',
      //   {
      //     text: 'Questionario aggiornato con successo!',
      //     color: 'success',
      //     timeout: 3500,
      //   },
      //   { root: true }
      // )
    } catch (error) {
      console.error('Error updating survey: ', error)
      throw error
    }
  },

  async pushJourney({ commit, state }, data) {
    try {
      commit('SET_LOADING', true)

      const journeys = [
        ...state.survey.journeys,
        {
          ...data,
          id: crypto.randomUUID(),
          order: state.survey.journeys.length,
          created_at: new Date(),
          previous_id: state.survey.journeys.at(-1)?.id ?? null,
          next_id: null,
        },
      ]

      await SurveyService.updateSurvey(state.survey.id, {
        journeys,
      })

      commit('SET_JOURNEYS', journeys)
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async popJourney({ commit, state }) {
    try {
      commit('SET_LOADING', true)

      const journeys = clone(state.survey.journeys)
      journeys.pop()

      await SurveyService.updateSurvey(state.survey.id, {
        journeys,
      })

      commit('SET_JOURNEYS', journeys)
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateJourney({ commit, state }, data) {
    try {
      commit('SET_LOADING', true)
      const journeys = state.survey.journeys.map((j) => (j.id === data.id ? data : j))

      await SurveyService.updateSurvey(state.survey.id, {
        journeys,
      })
      commit('SET_JOURNEYS', journeys)
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // TODO: Rimuovere (sostituita da popJourney)
  async removeLastJourney({ state, dispatch }) {
    await dispatch('popJourney')
    const survey = JSON.parse(JSON.stringify(state.survey))
    survey.journeys.pop()

    try {
      let surveysRef = db.collection('surveys')
      let surveyRef = await surveysRef.doc(survey.id)

      await surveyRef.update(survey)

      dispatch(
        'notification/showMessage',
        {
          text: 'Viaggio rimosso con successo!',
          color: 'success',
          timeout: 3500,
        },
        { root: true }
      )
    } catch (error) {
      console.error('Error deleting journey: ', error)
      throw error
    }
  },
  /* ** */
  async load({ commit }, id) {
    try {
      commit('SET_LOADING', true)
      const survey = await SurveyService.getSurvey(id)
      commit('SET_SURVEY', survey)
    } catch (error) {
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  goToNextDay({ state, commit }) {
    if (state.currentDay === 6) return
    commit('GO_TO_NEXT_DAY')
  },
  goToPrevDay({ state, commit }) {
    if (state.currentDay === 0) return
    commit('GO_TO_PREV_DAY')
  },
  goToDayOfLastJourney({ state, commit, getters }) {
    const day = getters['getLastJourney']?.day ?? 0
    commit('SET_CURRENT_DAY', day)
  },
  goToDay({ commit }, day) {
    commit('SET_CURRENT_DAY', day)
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
